import React, { useState, useCallback, useEffect } from 'react';
import {
  Folder, FileText, Image, File, ExternalLink, ChevronRight,
  Loader2, AlertCircle, FolderPlus, RefreshCw,
} from 'lucide-react';

interface DriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  modifiedTime?: string;
  webViewLink: string;
}

interface Props {
  applicationId: number;
  gdriveId?: string | null;
}

function getToken(): string {
  return localStorage.getItem('grant_crm_token') || localStorage.getItem('crm_token') || '';
}

function getMimeIcon(mimeType: string) {
  if (mimeType === 'application/vnd.google-apps.folder') {
    return <Folder size={18} className="text-amber-500 flex-shrink-0" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText size={18} className="text-red-500 flex-shrink-0" />;
  }
  if (mimeType.startsWith('image/')) {
    return <Image size={18} className="text-green-500 flex-shrink-0" />;
  }
  if (
    mimeType.includes('word') ||
    mimeType.includes('document') ||
    mimeType === 'application/vnd.google-apps.document'
  ) {
    return <FileText size={18} className="text-blue-500 flex-shrink-0" />;
  }
  return <File size={18} className="text-gray-400 flex-shrink-0" />;
}

function formatSize(size?: string): string {
  if (!size) return '';
  const bytes = parseInt(size, 10);
  if (isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const LoanDrivePanel: React.FC<Props> = ({ applicationId, gdriveId }) => {
  const [files, setFiles] = useState<DriveFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(gdriveId || null);
  const [rootFolderId, setRootFolderId] = useState<string | null>(gdriveId || null);
  const [breadcrumb, setBreadcrumb] = useState<{ id: string; name: string }[]>([]);
  const [hasDriveFolder, setHasDriveFolder] = useState<boolean>(!!gdriveId);

  const fetchFiles = useCallback(async (folderId: string | null) => {
    if (!folderId) return;
    setLoading(true);
    setError(null);
    try {
      const url = `/api/crm/applications/${applicationId}/drive-contents${folderId ? `?folderId=${folderId}` : ''}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setFiles(data.files || []);
      if (data.rootFolderId && !rootFolderId) {
        setRootFolderId(data.rootFolderId);
        setHasDriveFolder(true);
      }
    } catch (e: any) {
      setError(e.message || 'Błąd pobierania plików');
    } finally {
      setLoading(false);
    }
  }, [applicationId, rootFolderId]);

  // Auto-utwórz folder przy pierwszym otwarciu wniosku BGK
  useEffect(() => {
    if (!gdriveId && !hasDriveFolder) {
      handleCreateFolder();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasDriveFolder && currentFolderId) {
      fetchFiles(currentFolderId);
    }
  }, [hasDriveFolder, currentFolderId, fetchFiles]);

  const handleCreateFolder = async () => {
    setCreating(true);
    setError(null);
    try {
      const res = await fetch(`/api/crm/applications/${applicationId}/create-drive-folder`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRootFolderId(data.folderId);
      setCurrentFolderId(data.folderId);
      setHasDriveFolder(true);
    } catch (e: any) {
      setError(e.message || 'Błąd tworzenia folderu');
    } finally {
      setCreating(false);
    }
  };

  const handleFolderClick = (file: DriveFile) => {
    setBreadcrumb(prev => [...prev, { id: currentFolderId || '', name: breadcrumb.length === 0 ? 'Folder główny' : '...' }]);
    setCurrentFolderId(file.id);
  };

  const handleBreadcrumbBack = (index: number) => {
    const target = breadcrumb[index];
    setBreadcrumb(prev => prev.slice(0, index));
    setCurrentFolderId(target.id || rootFolderId);
  };

  const handleGoRoot = () => {
    setBreadcrumb([]);
    setCurrentFolderId(rootFolderId);
  };

  const folders = files.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
  const fileItems = files.filter(f => f.mimeType !== 'application/vnd.google-apps.folder');

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-200">
        <div className="flex items-center gap-2">
          <Folder size={18} className="text-amber-600" />
          <span className="font-semibold text-amber-900 text-sm">Dokumenty Drive</span>
          {breadcrumb.length > 0 && (
            <span className="text-xs text-amber-600">({breadcrumb.length + 1} poziom)</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {rootFolderId && (
            <a
              href={`https://drive.google.com/drive/folders/${rootFolderId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 hover:underline"
            >
              <ExternalLink size={12} />
              Otwórz w Drive
            </a>
          )}
          {hasDriveFolder && currentFolderId && (
            <button
              onClick={() => fetchFiles(currentFolderId)}
              className="p-1 text-amber-600 hover:text-amber-900 hover:bg-amber-100 rounded"
              title="Odśwież"
            >
              <RefreshCw size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Breadcrumb */}
      {breadcrumb.length > 0 && (
        <div className="flex items-center gap-1 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 overflow-x-auto">
          <button onClick={handleGoRoot} className="hover:text-gray-800 hover:underline whitespace-nowrap">
            Folder główny
          </button>
          {breadcrumb.slice(1).map((crumb, i) => (
            <React.Fragment key={i}>
              <ChevronRight size={10} />
              <button
                onClick={() => handleBreadcrumbBack(i + 1)}
                className="hover:text-gray-800 hover:underline whitespace-nowrap"
              >
                {crumb.name}
              </button>
            </React.Fragment>
          ))}
          <ChevronRight size={10} />
          <span className="text-gray-800 font-medium">Bieżący folder</span>
        </div>
      )}

      {/* Content */}
      <div className="p-3">
        {!hasDriveFolder ? (
          <div className="flex flex-col items-center py-6 gap-3">
            {creating ? (
              <>
                <Loader2 size={28} className="animate-spin text-amber-500" />
                <p className="text-sm text-amber-700">Tworzenie folderu Drive...</p>
              </>
            ) : (
              <>
                <Folder size={36} className="text-gray-300" />
                <p className="text-sm text-gray-500">Nie udało się utwórzyć folderu</p>
                <button
                  onClick={handleCreateFolder}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700 transition-colors"
                >
                  <FolderPlus size={14} />
                  Spróbuj ponownie
                </button>
                {error && (
                  <div className="flex items-center gap-1 text-xs text-red-600">
                    <AlertCircle size={12} />
                    {error}
                  </div>
                )}
              </>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-6 gap-2 text-gray-400">
            <Loader2 size={16} className="animate-spin" />
            <span className="text-sm">Ładowanie plików...</span>
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 py-4 px-3 bg-red-50 rounded text-sm text-red-700">
            <AlertCircle size={14} />
            {error}
          </div>
        ) : files.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Folder jest pusty</p>
        ) : (
          <div className="space-y-0.5">
            {/* Folders first */}
            {folders.map(f => (
              <button
                key={f.id}
                onClick={() => handleFolderClick(f)}
                className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-amber-50 group transition-colors"
              >
                {getMimeIcon(f.mimeType)}
                <span className="text-sm text-gray-800 group-hover:text-amber-800 truncate flex-1">{f.name}</span>
                <ChevronRight size={12} className="text-gray-300 group-hover:text-amber-400" />
              </button>
            ))}
            {/* Files */}
            {fileItems.map(f => (
              <a
                key={f.id}
                href={f.webViewLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 w-full text-left px-2 py-1.5 rounded hover:bg-gray-50 group transition-colors"
              >
                {getMimeIcon(f.mimeType)}
                <span className="text-sm text-gray-700 group-hover:text-gray-900 truncate flex-1">{f.name}</span>
                {f.size && (
                  <span className="text-xs text-gray-400 flex-shrink-0">{formatSize(f.size)}</span>
                )}
                <ExternalLink size={11} className="text-gray-300 group-hover:text-gray-500 flex-shrink-0" />
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
